// src/services/battleTimer.js
// =====================================================
// Battle timing and completion logic with TEST_MODE
// =====================================================

// V3-safe portfolio helpers
import { safePortfolioArray } from '../utils/portfolioHelpers';

/**
 * 🚀 TEST_MODE: Set to true for 24-hour battles (fast testing)
 * Set to false for 5-day battles (production)
 */
export const TEST_MODE = true; // Currently: 1-day battles for user testing

/**
 * Duration constants
 */
export const DAY_DURATION = TEST_MODE ? (24 * 60 * 60 * 1000) : (24 * 60 * 60 * 1000); // 24 hours for both test and production
export const BATTLE_DURATION = 1 * DAY_DURATION; // 1 day battles for testing (change to 5 for production)

// XP Constants
const BASE_XP_WIN = 100;
const BASE_XP_LOSS = 25;
const MAX_BONUS_XP = 100;

// =====================================================
// BATTLE STATUS
// =====================================================

/**
 * Checks if a battle has started
 */
export function hasBattleStarted(battle) {
  if (!battle || !battle.startDate) return false;
  return Date.now() >= new Date(battle.startDate).getTime();
}

/**
 * Checks if a battle has ended (passed endDate)
 */
export function hasBattleEnded(battle) {
  if (!battle || !battle.endDate) return false;
  return Date.now() >= new Date(battle.endDate).getTime();
}

/**
 * Checks if battle is complete (alias for hasBattleEnded)
 */
export function isBattleComplete(battle) {
  return hasBattleEnded(battle);
}

/**
 * Check if a battle just completed (useful for triggering winner logic)
 * @param {Object} battle - Battle object
 * @returns {boolean}
 */
export function isJustCompleted(battle) {
  if (!battle.opponent || battle.result) {
    return false; // Already processed or no opponent
  }

  return getBattleStatus(battle) === 'completed';
}

/**
 * Gets battle status
 * @returns 'waiting' | 'active' | 'completed'
 */
export function getBattleStatus(battle) {
  if (!battle) return 'waiting';

  // V3/V4 BaggerBomb battles use state.status directly
  if (battle._v === 3 || battle._v === 4) {
    const status = battle.state?.status;
    if (status === 'waiting') return 'waiting';
    if (status === 'active') return 'active';
    if (status === 'completed') return 'completed';
    // Fall through to time-based check if status not set
  }

  // If no opponent yet, still waiting
  if (!battle.opponent) return 'waiting';

  // If startDate not set yet, waiting (check both locations)
  const startDate = battle.startDate || battle.timeline?.startDate;
  if (!startDate) return 'waiting';

  // If battle has ended, completed (check both locations)
  const endDate = battle.endDate || battle.timeline?.endDate;
  if (endDate && Date.now() >= new Date(endDate).getTime()) return 'completed';

  // If battle has started but not ended, active
  if (Date.now() >= new Date(startDate).getTime()) return 'active';

  // Default to waiting
  return 'waiting';
}

// =====================================================
// TIME REMAINING
// =====================================================

/**
 * Gets remaining time in milliseconds
 */
export function getRemainingTime(battle) {
  if (!battle || !battle.endDate) return 0;
  const remaining = new Date(battle.endDate).getTime() - Date.now();
  return Math.max(0, remaining);
}

/**
 * Formats remaining time as human-readable string
 * Examples: "4 days, 3 hours", "23 hours, 45 minutes", "15 minutes"
 */
export function formatTimeRemaining(battle) {
  const remaining = getRemainingTime(battle);

  if (remaining === 0) {
    return 'Battle Complete';
  }

  const days = Math.floor(remaining / DAY_DURATION);
  const hours = Math.floor((remaining % DAY_DURATION) / (60 * 60 * 1000));
  const minutes = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000));

  // For battles less than 5 days (like 24-hour test mode)
  if (days === 0) {
    if (hours > 0) {
      return `${hours} hour${hours !== 1 ? 's' : ''}, ${minutes} min remaining`;
    }
    return `${minutes} min remaining`;
  }

  // For longer battles (5+ days)
  if (days > 0) {
    return `${days} day${days !== 1 ? 's' : ''}, ${hours} hour${hours !== 1 ? 's' : ''} remaining`;
  }
  
  return `${minutes} min remaining`;
}

/**
 * Gets the current day of the battle (1-5)
 */
export function getCurrentDay(battle) {
  if (!battle || !battle.startDate) return 0;

  const startTime = new Date(battle.startDate).getTime();
  const currentTime = Date.now();
  const elapsed = currentTime - startTime;

  // Calculate which day we're on (1-5)
  const day = Math.floor(elapsed / DAY_DURATION) + 1;

  // Cap at day 5
  return Math.min(day, 5);
}

/**
 * Formats milliseconds as MM:SS
 */
export function formatTime(ms) {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

// =====================================================
// WINNER DETERMINATION
// =====================================================

/**
 * Calculate portfolio return percentage
 * @param {Array|Object} portfolio - Array of portfolio assets or V3 tiered object
 * @param {Object} currentPrices - Current prices for all assets {symbol: price}
 * @returns {number} - Return percentage
 */
export function calculatePortfolioReturn(portfolio, currentPrices) {
  // Flatten portfolio if it's a V3 tiered object (V3-safe via portfolioHelpers)
  const flatPortfolio = safePortfolioArray(portfolio);

  if (!flatPortfolio || flatPortfolio.length === 0) {
    return 0;
  }

  // Calculate initial value and current value
  let initialValue = 0;
  let currentValue = 0;

  flatPortfolio.forEach(asset => {
    if (!asset) return;
    const shares = (asset.amount || 0) / (asset.price || 1);
    const currentPrice = currentPrices?.[asset.symbol] || asset.price || 0;

    initialValue += asset.amount || 0;
    currentValue += shares * currentPrice;
  });

  if (initialValue === 0) return 0;

  // Return percentage: (current - initial) / initial * 100
  return ((currentValue - initialValue) / initialValue) * 100;
}

/**
 * Determine the winner of a completed battle
 * @param {Object} battle - Battle object with creatorPortfolio and opponentPortfolio
 * @param {Object} currentPrices - Current prices for all assets
 * @returns {Object} - { winner, loser, creatorReturn, opponentReturn, margin }
 */
export function determineWinner(battle, currentPrices) {
  const creatorReturn = calculatePortfolioReturn(battle.creatorPortfolio, currentPrices);
  const opponentReturn = calculatePortfolioReturn(battle.opponentPortfolio, currentPrices);

  const margin = Math.abs(creatorReturn - opponentReturn);

  if (creatorReturn > opponentReturn) {
    return {
      winner: battle.creator,
      loser: battle.opponent,
      creatorReturn: Number(creatorReturn.toFixed(2)),
      opponentReturn: Number(opponentReturn.toFixed(2)),
      margin: Number(margin.toFixed(2))
    };
  } else {
    return {
      winner: battle.opponent,
      loser: battle.creator,
      creatorReturn: Number(creatorReturn.toFixed(2)),
      opponentReturn: Number(opponentReturn.toFixed(2)),
      margin: Number(margin.toFixed(2))
    };
  }
}

// =====================================================
// XP CALCULATION
// =====================================================

/**
 * Calculate XP earned from a battle
 * @param {boolean} won - Did the player win?
 * @param {number} margin - Victory/loss margin (percentage points)
 * @returns {number} - XP earned
 */
export function calculateXP(won, margin) {
  if (won) {
    // Winner gets base XP + bonus based on margin
    const bonus = Math.min(margin * 10, MAX_BONUS_XP); // 10 XP per % margin, max 100
    return Math.floor(BASE_XP_WIN + bonus);
  } else {
    // Loser gets base XP (participation)
    return BASE_XP_LOSS;
  }
}

/**
 * THE RANK LADDER — the single source for both the rungs and their
 * thresholds, ascending.
 *
 * This is the only place either exists. It is exported because three
 * surfaces need the same answer and used to each carry their own copy:
 * determineRank below (which ASSIGNS the rank on battle settlement), the
 * app-level XP progress modal, and ProfileScreen's stat block. The modal's
 * copy had drifted to a different six-rung list entirely — it shared only
 * two rungs with this one, so it named ranks the game cannot award and its
 * "XP to next rank" figure went negative past 10,000 XP
 * (docs/audits/20260915_BUILD_CRASH_CLASS_A1_A4.md §8 F1 / F4).
 *
 * Adding or retuning a rung is a one-line edit here and nowhere else.
 */
export const RANK_LADDER = [
  { rank: 'Beginner', minXp: 0 },
  { rank: 'Veteran', minXp: 500 },
  { rank: 'Expert', minXp: 2000 },
  { rank: 'Master', minXp: 5000 },
];

/**
 * Check if user should rank up based on XP
 * @param {number} xp - Current XP
 * @returns {string} - New rank
 */
export function determineRank(xp) {
  // Top rung down, which is the same order the hardcoded chain used to test
  // in — so every input lands on the rung it always did, including a NaN or
  // undefined xp, where no comparison holds and the floor rung wins.
  for (let i = RANK_LADDER.length - 1; i > 0; i--) {
    if (xp >= RANK_LADDER[i].minXp) return RANK_LADDER[i].rank;
  }
  return RANK_LADDER[0].rank;
}

/**
 * Where a player sits on the ladder, and what is above them — everything a
 * surface needs to describe rank progress, derived from ONE input so the
 * pieces cannot disagree with each other or with the assigned rank
 * (BUILD_RULES §9).
 *
 * `xpToNextRank` is positive by construction: determineRank only returns
 * rung i when xp is below rung i+1's threshold. At the top rung there is no
 * next rung, and both `nextRank` and `xpToNextRank` are null rather than a
 * placeholder a caller might render.
 *
 * @param {number} xp - Current XP
 * @returns {{rank: string, nextRank: string|null, xpToNextRank: number|null, isMaxRank: boolean}}
 */
export function getRankProgress(xp) {
  const rank = determineRank(xp);
  const next = RANK_LADDER[RANK_LADDER.findIndex((r) => r.rank === rank) + 1] || null;
  return {
    rank,
    nextRank: next ? next.rank : null,
    xpToNextRank: next ? next.minXp - xp : null,
    isMaxRank: next === null,
  };
}

// =====================================================
// BATTLE PROCESSING
// =====================================================

/**
 * Process a newly completed battle
 * Updates the battle with result data but does NOT update user stats
 * (User stats should be updated separately based on current user)
 * 
 * @param {Object} battle - Battle object
 * @param {Object} currentPrices - Current prices for all assets
 * @returns {Object} - Updated battle with result
 */
export function processCompletedBattle(battle, currentPrices) {
  // Determine winner
  const result = determineWinner(battle, currentPrices);

  // Calculate XP for both players
  const creatorIsWinner = result.winner === battle.creator;
  const creatorXP = calculateXP(creatorIsWinner, result.margin);
  const opponentXP = calculateXP(!creatorIsWinner, result.margin);

  // Return updated battle object
  return {
    ...battle,
    status: 'completed',
    completedAt: new Date().toISOString(),
    result: {
      ...result,
      xpAwarded: {
        [battle.creator]: creatorXP,
        [battle.opponent]: opponentXP
      }
    }
  };
}

// =====================================================
// UTILITY FUNCTIONS
// =====================================================

/**
 * Get start and end timestamps for a battle
 * @param {Object} battle - Battle object
 * @returns {Object} - { startTime, endTime }
 */
export function getBattleTimes(battle) {
  const startTime = new Date(battle.startDate).getTime();
  const endTime = startTime + BATTLE_DURATION;

  return {
    startTime: new Date(startTime),
    endTime: new Date(endTime)
  };
}

/**
 * Format date for display
 * @param {string|Date} date - Date to format
 * @returns {string}
 */
export function formatDate(date) {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

/**
 * Get battle duration in human-readable format
 * @returns {string}
 */
export function getBattleDurationText() {
  if (TEST_MODE) {
    return '1 day';
  }
  return '5 days';
}

// =====================================================
// EXPORTS
// =====================================================

export default {
  TEST_MODE,
  BATTLE_DURATION,
  DAY_DURATION,
  hasBattleStarted,
  hasBattleEnded,
  isBattleComplete,
  isJustCompleted,
  getBattleStatus,
  getRemainingTime,
  formatTimeRemaining,
  getCurrentDay,
  formatTime,
  calculatePortfolioReturn,
  determineWinner,
  calculateXP,
  determineRank,
  getRankProgress,
  RANK_LADDER,
  processCompletedBattle,
  getBattleTimes,
  formatDate,
  getBattleDurationText
};